import type { ColorRamp } from 'ui/styles/functions/colorRampGenerator/colorRamp';
import generateColorRamp from 'ui/styles/functions/colorRampGenerator/colorRamp';

const COLOR_RAMP_GENERATION_COEFFICIENT = 0.7;

const colorRampValues = {
  grey: ['#FAFAFA', '#858589', '#161617'],
  purpleGrey: ['#F7F7FC', '#737791', '#222233'],
  red: ['#FEF5F6', '#FD5254', '#471819'],
  green: ['#F2FCF9', '#09C590', '#00231B'],
  yellow: ['#FFF8ED', '#EEAD51', '#2E2516'],
  blue: ['#F2F6FD', '#467EE5', '#0E192F'],
  brand: ['#F1F0FF', '#716BFF', '#1B1A3D'],
  newBrand: ['#FCFEF1', '#D9EE50', '#212504'],
  newGrey: ['#F5F5F5', '#858589', '#161617'],
  purple: ['#F9F5FE', '#9B6DFF', '#2B1A4D'],
  orange: ['#FFF8F2', '#FF9B50', '#4D2E1A'],
  pink: ['#FDF0F6', '#E12C7D', '#280112'],
};

/**
 * Zinc: Dark mode neutral scale
 *
 * NOTE: These colors are NOT from design. They are placeholder values
 * until design provides the official dark mode color palette.
 *
 * Manually defined to preserve exact color values used in the dark theme.
 *
 * Dark end (backgrounds):
 *   100 → Page background
 *   90  → Card/elevated surfaces
 *   80  → Hover states, secondary surfaces
 *   70  → Accent surfaces, borders
 *
 * Light end (text/icons):
 *   05  → Button text
 *   10  → Primary text/icons
 *   30  → Secondary text/icons
 *   50  → Helper text, disabled icons
 *   60  → Placeholder, disabled text
 */
const zinc: ColorRamp = {
  '05': '#F5F5F5', // Button text, lightest
  '10': '#EDEDED', // Primary text/icons
  '20': '#D4D4D8',
  '30': '#A1A1AA', // Secondary text/icons
  '40': '#8E8E93',
  '50': '#71717A', // Helper text, disabled icons
  '60': '#52525B', // Placeholder, disabled text
  '70': '#27272A', // Surface grey, borders
  '80': '#18181B', // Dark grey, hover states
  '90': '#121217', // Card backgrounds
  '100': '#050509', // Page background
};

type MAIN_COLORS = keyof typeof colorRampValues;

const colors = Object.entries(colorRampValues).reduce(
  (prev, [color, [start, target, end]]) => {
    prev[color as MAIN_COLORS] = generateColorRamp(
      start,
      target,
      end,
      COLOR_RAMP_GENERATION_COEFFICIENT,
    );
    return prev;
  },
  {} as { [key in MAIN_COLORS]: ColorRamp },
);

const baseColors = {
  white: '#FFFFFF',
  offWhite: '#FCFCFC',
  black: '#000000',
  ...colors,
  zinc, // dark mode neutral scale
};

export default baseColors;
